import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CollectorAvatar } from "@/components/CollectorAvatar";
import { ReassignLeadDialog } from "@/components/company/ReassignLeadDialog";
import { LeadWaitTimer } from "@/components/collector/LeadWaitTimer";
import { toast } from "sonner";
import { Mail, Phone, PhoneCall, CheckCircle2, XCircle, MapPin, RefreshCw, Clock, ChevronRight, ArrowRightLeft } from "lucide-react";
import { labelFor } from "@/lib/services";

const DECLINE_REASONS = [
  { key: "too_far", label: "Too far away" },
  { key: "wrong_service", label: "Service not offered" },
  { key: "busy", label: "Too busy" },
  { key: "other", label: "Other" },
];

const STATUS_LABELS = {
  requested: "New",
  contacted: "Contacted",
  confirmed: "Confirmed",
  taken: "Contacted by Another Collector",
};

const STATUS_CLS = {
  requested: "bg-[#E9C46A]/30 text-[#1E3F33]",
  contacted: "bg-[#2D6A4F]/15 text-[#2D6A4F]",
  taken: "bg-[#CC5A3A]/12 text-[#CC5A3A]",
};

const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

const cardCls = (l) => {
  if (l.status === "taken") return "border-[#1E3F33]/10 bg-[#F0EFEA]/60 opacity-90";
  if (l.reopened && l.status === "requested") return "border-[#E9C46A] ring-1 ring-[#E9C46A]/40 bg-[#E9C46A]/5";
  return "border-[#1E3F33]/10";
};

const LeadTimeline = ({ l }) => (
  <details className="mt-3" data-testid={`lead-timeline-${l.id}`}>
    <summary className="text-[11px] text-[#8A9A86] cursor-pointer hover:text-[#2D6A4F] inline-flex items-center gap-1 select-none">
      <Clock className="w-3 h-3"/> Activity ({l.timeline.length})
    </summary>
    <ol className="mt-2 border-l border-[#1E3F33]/10 pl-3 space-y-1.5">
      {l.timeline.map((ev, i) => (
        <li key={i} className="text-[11px] text-[#5C6B64]">
          <span className="text-[#1E3F33]">{ev.label}</span>
          <span className="text-[#8A9A86]"> · {fmtWhen(ev.at)}</span>
        </li>
      ))}
    </ol>
  </details>
);

const DeclinePanel = ({ l, onDecline, onCancel }) => (
  <div className="w-full rounded-lg border border-[#1E3F33]/10 p-2.5 bg-[#F0EFEA]/40" data-testid={`lead-decline-panel-${l.id}`}>
    <div className="text-[11px] font-medium text-[#1E3F33] mb-1.5">Why can't you take this?</div>
    <div className="flex flex-wrap gap-1.5">
      {DECLINE_REASONS.map((r) => (
        <button key={r.key} type="button" data-testid={`lead-decline-reason-${r.key}-${l.id}`} onClick={() => onDecline(r.key)} className="text-[11px] px-2.5 py-1 rounded-full border border-[#1E3F33]/15 text-[#5C6B64] hover:border-[#CC5A3A] hover:text-[#CC5A3A] transition-colors">{r.label}</button>
      ))}
    </div>
    <button type="button" data-testid={`lead-decline-cancel-${l.id}`} onClick={onCancel} className="text-[11px] text-[#8A9A86] hover:underline mt-2">Cancel</button>
  </div>
);

const MatchedTo = ({ l }) => (
  <Link to={`/dashboard/company/collectors/${l.collector_id}`} data-testid={`lead-collector-${l.id}`}
    className="-mx-1 mb-3 flex items-center gap-2.5 rounded-lg px-1 py-1 hover:bg-[#2D6A4F]/8 transition-colors group">
    <CollectorAvatar name={l.collector_name} photo={l.collector_photo_url} />
    <span className="min-w-0 flex-1">
      <span className="block text-[10px] uppercase tracking-wider text-[#8A9A86] leading-none">Matched to</span>
      <span className="block text-sm font-medium text-[#1E3F33] truncate group-hover:underline underline-offset-2 mt-0.5">{l.collector_name}</span>
    </span>
    <ChevronRight className="w-4 h-4 text-[#8A9A86] group-hover:text-[#1E3F33] shrink-0" />
  </Link>
);

export const LeadCard = ({ l, onChanged, showCollector, canReassign = false }) => {
  const [declining, setDeclining] = useState(false);
  const [reassigning, setReassigning] = useState(false);

  const run = async (fn, msg) => {
    try { const { data } = await fn(); toast.success(typeof msg === "function" ? msg(data) : msg); await onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const markContacted = () => run(() => api.patch(`/leads/${l.id}/contacted`), "Marked as contacted");
  const decline = (reason) => run(() => api.patch(`/leads/${l.id}/decline`, { reason }), (d) => {
    const n = d?.rematched_count || 0;
    return n > 0 ? `Removed — we've alerted ${n} more collector${n === 1 ? "" : "s"} nearby` : "Request removed from your dashboard";
  });
  const quickMove = async () => {
    const b = l.best_match;
    try {
      const { data } = await api.post(`/company/leads/${l.id}/reassign`, { collector_id: b.id });
      const prev = data.previous_collector;
      toast.success(`Lead moved to ${b.name}`, {
        action: prev ? { label: "Undo", onClick: () => run(() => api.post(`/company/leads/${l.id}/reassign`, { collector_id: prev.id }), `Moved back to ${prev.name}`) } : undefined,
      });
      await onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const release = () => {
    if (!window.confirm("Release this lead? It will reopen to other collectors and you'll lose access to this customer's contact info.")) return;
    run(() => api.patch(`/leads/${l.id}/decline`, { reason: "released" }), (d) => {
      const n = d?.reopened_count || 0;
      return n > 0 ? `Lead released — reopened to ${n} collector${n === 1 ? "" : "s"}` : "Lead released";
    });
  };

  return (
    <li className={`border rounded-xl p-4 ${cardCls(l)}`} data-testid={`lead-${l.id}`}>
      {showCollector && l.collector_name && <MatchedTo l={l} />}
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-[#1E3F33]">{l.customer_name || (l.status === "taken" ? "Lead unavailable" : "Customer")}</div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_CLS[l.status] || "bg-[#1E3F33]/10 text-[#1E3F33]"}`} data-testid={`lead-status-${l.id}`}>{STATUS_LABELS[l.status] || l.status}</span>
      </div>
      {l.status === "requested" && <div className="mt-1.5"><LeadWaitTimer at={l.created_at} id={l.id} /></div>}
      {l.reopened && l.status === "requested" && (
        <div className="mt-1.5" data-testid={`lead-reopened-${l.id}`}>
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#E9C46A]/30 text-[#8A5A00]">
            <RefreshCw className="w-2.5 h-2.5"/> Reopened · previously claimed
          </span>
        </div>
      )}
      <div className="text-xs text-[#5C6B64] mt-1">When: {l.when_label}</div>
      <div className="text-xs text-[#5C6B64] mt-0.5">Zip: {(l.zip_codes || []).join(", ")}</div>
      {typeof l.distance_miles === "number" && (
        <div className="text-xs text-[#2D6A4F] font-medium mt-0.5 inline-flex items-center gap-1" data-testid={`lead-distance-${l.id}`}>
          <MapPin className="w-3 h-3"/> ~{l.distance_miles} mi away
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {(l.services || []).map((s) => (
          <span key={s} className="text-[10px] bg-[#F0EFEA] text-[#1E3F33] rounded-full px-2 py-0.5">{labelFor(s)}</span>
        ))}
      </div>
      {l.notes && <div className="text-xs text-[#5C6B64] mt-2 italic">"{l.notes}"</div>}
      <div className="flex flex-col gap-1 mt-3 text-xs">
        {l.customer_email && <a href={`mailto:${l.customer_email}`} className="text-[#CC5A3A] hover:underline inline-flex items-center gap-1.5"><Mail className="w-3 h-3"/> {l.customer_email}</a>}
        {l.customer_phone && <a href={`tel:${l.customer_phone}`} className="text-[#CC5A3A] hover:underline inline-flex items-center gap-1.5"><Phone className="w-3 h-3"/> {l.customer_phone}</a>}
      </div>
      {l.status === "requested" && (
        <div className="mt-3 flex flex-col gap-2 items-start">
          <button data-testid={`lead-contacted-${l.id}`} onClick={markContacted} className="btn-primary text-xs py-2 inline-flex items-center gap-2">
            <PhoneCall className="w-3 h-3"/> I contacted this customer
          </button>
          {canReassign && l.best_match && (
            <button data-testid={`lead-best-match-${l.id}`} onClick={quickMove} title={`Offers the service, covers the area, ${l.best_match.open_leads} open lead${l.best_match.open_leads === 1 ? "" : "s"}`}
              className="text-xs rounded-full border border-[#2D6A4F]/30 bg-[#2D6A4F]/10 text-[#1E3F33] hover:bg-[#2D6A4F]/20 transition-colors pl-1 pr-3 py-1 inline-flex items-center gap-2">
              <CollectorAvatar name={l.best_match.name} photo={l.best_match.photo_url} size="w-5 h-5" />
              <span><span className="text-[#2D6A4F] font-semibold">Best match:</span> Move to {l.best_match.name.split(" ")[0]}</span>
            </button>
          )}
          {canReassign && (
            <button data-testid={`lead-reassign-${l.id}`} onClick={() => setReassigning(true)} className="text-xs text-[#2D6A4F] font-medium hover:underline underline-offset-2 inline-flex items-center gap-1.5">
              <ArrowRightLeft className="w-3 h-3"/> {l.best_match ? "Choose someone else" : "Reassign to a teammate"}
            </button>
          )}
          {declining ? (
            <DeclinePanel l={l} onDecline={(r) => { setDeclining(false); decline(r); }} onCancel={() => setDeclining(false)} />
          ) : (
            <button data-testid={`lead-decline-${l.id}`} onClick={() => setDeclining(true)} className="text-xs text-[#8A9A86] hover:text-[#E63946] inline-flex items-center gap-1.5">
              <XCircle className="w-3 h-3"/> Cannot provide requested services
            </button>
          )}
        </div>
      )}
      {l.status === "contacted" && (
        <div className="mt-3 flex flex-col gap-2 items-start">
          <div className="text-xs text-[#8A9A86]">Waiting for the customer to confirm contact.</div>
          <button data-testid={`lead-release-${l.id}`} onClick={release} className="text-xs text-[#8A9A86] hover:text-[#E63946] inline-flex items-center gap-1.5">
            <XCircle className="w-3 h-3"/> Can't complete — release this lead
          </button>
        </div>
      )}
      {l.status === "confirmed" && (
        <div className="mt-3 text-xs text-[#2D6A4F] inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3"/> Contact confirmed by customer{l.reviewed ? " · reviewed" : ""}.</div>
      )}
      {l.status === "taken" && (
        <div className="mt-3 text-xs text-[#CC5A3A] inline-flex items-start gap-1.5" data-testid={`lead-taken-${l.id}`}><XCircle className="w-3 h-3 mt-0.5 shrink-0"/> Another collector has already confirmed contact with this customer. This lead is now closed to you.</div>
      )}
      {(l.timeline || []).length > 0 && <LeadTimeline l={l} />}
      {canReassign && l.status === "requested" && <ReassignLeadDialog lead={l} open={reassigning} onOpenChange={setReassigning} onDone={onChanged} />}
    </li>
  );
};
